import { Button, Grid } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Permission } from '../../../enums';
import type { AssetForm } from '../../../store/forms';
import { fuelUserAccessSliceSelector } from '../../../store/slices/fuelUserAccess';

interface IProps {
  assetForm: AssetForm;
  assetFormValues: any; //  react hook form
  onSaveClick: (form: AssetForm) => void;
  onCancelClick: () => void;
  setThumbnail?: () => void;
  uploadThumbnail?: (file: File) => void;
  onFormChanged?: () => void;
  isNew?: boolean;
  onAnalyzeWithAI?: () => void;
  rightOpen?: boolean;
  expandRightSide?: (rightOpen) => void;
}
const AssetDetailsActions = ({
  assetForm,
  assetFormValues,
  isNew = false,
  onSaveClick,
  onCancelClick,
  setThumbnail,
  uploadThumbnail,
  onFormChanged,
  onAnalyzeWithAI,
  rightOpen = false,
  expandRightSide,
}: IProps) => {
  const flags = useFlags();
  const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const [isNestedChannel, setIsNestedChannel] = useState(assetForm.source === 'NestedChannel');
  const [isEditingDisabled, setIsEditingDisabled] = useState(false); // assetForm.permissions &
  const [isVideo, setIsVideo] = useState<boolean>(assetForm.schemaType === 'Video');
  const [isImage, setIsImage] = useState<boolean>(assetForm.schemaType === 'Image');
  const [isClipGroupSource, setIsClipGroupSource] = useState(false);
  const [isAIAnalyzedApplied, setIsAIAnalyzedApplied] = useState(false);
  const [fuelAIpocFeatureEnable, setFuelAIpocFeatureEnable] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);

  const analyzeWithAI = (evt) => {
    if (onAnalyzeWithAI) {
      setIsAIAnalyzedApplied(true);
      onAnalyzeWithAI();
    }
  };
  const onSave: SubmitHandler<AssetForm> = async (form: AssetForm) => {
    setIsAIAnalyzedApplied(false);
    onSaveClick(form);
  };

  const onCancel = () => {
    setIsAIAnalyzedApplied(false);
    onCancelClick();
  };
  const onBrowseButtonClick = () => {
    // `current` points to the mounted file input element
    if (fileInput && fileInput.current) {
      fileInput.current.click();
    }
  };

  const processFileChange = (fileList: FileList) => {
    if (fileList.length === 0) return;

    // only supporting single file upload...multiple files are possible with the drag and drop, so just take the first file...
    const file = fileList.item(0);
    // console.log('file', file);
    setThumbnailFile(file);
    if (uploadThumbnail) uploadThumbnail(file);
    // if (fileInput && fileInput.current) fileInput.current.value = '';
  };

  useEffect(() => {
    // reset logic here as assetForm.resetForm is called here
    // console.log('assetForm changed', isInitialized, assetForm);

    setIsVideo(assetForm.schemaType === 'Video');
    setIsImage(assetForm.schemaType === 'Image');
    setIsClipGroupSource(assetForm.source === 'ClipGroup');
  }, [assetForm]);

  useEffect(() => {
    setFuelAIpocFeatureEnable(flags['fuel-ai-poc-features']);
  }, []);

  return (
    <>
      {!isClipGroupSource && (
        <Grid container className="section-box" spacing={0}>
          <Grid xs={8} item sx={{ marginBottom: '0px' }}>
            {!isNestedChannel && !isNew && (
              <div>
                {assetForm.schemaType === 'Video' && (
                  <Button
                    color="primary"
                    variant="contained"
                    sx={{ mr: 1 }}
                    onClick={setThumbnail}
                    disabled={assetForm.permissions && !assetForm.permissions.has(Permission.EditAsset)}
                  >
                    Set Thumbnail
                  </Button>
                )}
                <input
                  className="file-input"
                  ref={fileInput}
                  id="uploalThumbnail"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => processFileChange(e.target.files)}
                />
                <Button
                  color="primary"
                  variant="contained"
                  sx={{ mr: 1 }}
                  onClick={onBrowseButtonClick}
                  disabled={assetForm.permissions && !assetForm.permissions.has(Permission.EditAsset)}
                >
                  Upload Thumbnail
                </Button>
                {assetForm.schemaType === 'Video' &&
                  permissions &&
                  permissions.has(Permission.OUSuperAdmin) &&
                  fuelAIpocFeatureEnable && (
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={analyzeWithAI}
                      disabled={assetForm.permissions && !assetForm.permissions.has(Permission.EditAsset)}
                    >
                      GENERATE WITH AI
                    </Button>
                  )}
              </div>
            )}
          </Grid>
          <Grid xs={4} item container justifyContent="flex-end" sx={{ marginBottom: '0px' }}>
            <Button variant="contained" sx={{ mr: 1 }} className="cancel-button" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained" onClick={assetFormValues.handleSubmit(onSave)}>
              Save
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default AssetDetailsActions;
